import { useState } from "react";
import { useOrgSettings } from "@/lib/org-settings-context";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowUpRight,
  ArrowDownRight,
  ChevronDown,
  ChevronUp,
  Receipt,
  ShoppingBag,
  Utensils,
  Plane,
  Zap,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { staggerContainer, staggerItem } from "@/lib/animations";

interface Transaction {
  id: string;
  type: "CREDIT" | "DEBIT";
  amountCents: number;
  merchantName?: string;
  description?: string;
  createdAt: string;
  status?: string;
}

interface TransactionTimelineProps {
  transactions: Transaction[];
  groupByDate?: boolean;
  showDetails?: boolean;
  limit?: number;
}

export function TransactionTimeline({
  transactions,
  groupByDate = true,
  showDetails = false,
  limit,
}: TransactionTimelineProps) {
  const { formatCurrency } = useOrgSettings();
  const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set());
  const [expandedTxns, setExpandedTxns] = useState<Set<string>>(new Set());

  // Group transactions by date
  const groupedTransactions = groupByDate
    ? transactions.reduce((acc, txn) => {
        const date = new Date(txn.createdAt).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        });
        if (!acc[date]) acc[date] = [];
        acc[date].push(txn);
        return acc;
      }, {} as Record<string, Transaction[]>)
    : { All: transactions };

  const toggleDate = (date: string) => {
    const newExpanded = new Set(expandedDates);
    if (newExpanded.has(date)) {
      newExpanded.delete(date);
    } else {
      newExpanded.add(date);
    }
    setExpandedDates(newExpanded);
  };

  const toggleTransaction = (id: string) => {
    const newExpanded = new Set(expandedTxns);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedTxns(newExpanded);
  };

  const getIcon = (txn: Transaction) => {
    const text = (txn.merchantName || txn.description || "").toLowerCase();

    if (text.includes("shopping") || text.includes("amazon") || text.includes("flipkart")) {
      return ShoppingBag;
    }
    if (text.includes("food") || text.includes("restaurant") || text.includes("zomato")) {
      return Utensils;
    }
    if (text.includes("travel") || text.includes("flight") || text.includes("uber")) {
      return Plane;
    }
    if (text.includes("bill") || text.includes("utility") || text.includes("electricity")) {
      return Zap;
    }
    return Receipt;
  };

  const formatAmount = (cents: number) => formatCurrency(cents);

  const formatTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const isToday = (dateStr: string) => {
    const date = new Date(dateStr);
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isYesterday = (dateStr: string) => {
    const date = new Date(dateStr);
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return date.toDateString() === yesterday.toDateString();
  };

  const getDateLabel = (dateStr: string) => {
    if (isToday(dateStr)) return "Today";
    if (isYesterday(dateStr)) return "Yesterday";
    return dateStr;
  };

  return (
    <motion.div
      variants={staggerContainer}
      initial="initial"
      animate="animate"
      className="space-y-4"
    >
      {Object.entries(groupedTransactions).map(([date, txns]) => {
        const isExpanded = !groupByDate || expandedDates.has(date) || isToday(date);
        const displayTxns = limit && !isExpanded ? txns.slice(0, limit) : txns;
        const totalAmount = txns.reduce((sum, t) => sum + t.amountCents, 0);

        return (
          <motion.div key={date} variants={staggerItem} className="space-y-2">
            {/* Date header */}
            {groupByDate && (
              <div
                className={cn(
                  "flex items-center justify-between py-2 cursor-pointer hover:bg-muted/50 rounded-lg px-3 transition-colors",
                  txns.length > 3 && "cursor-pointer"
                )}
                onClick={() => txns.length > 3 && toggleDate(date)}
              >
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold">{getDateLabel(date)}</h3>
                  <Badge variant="secondary" className="text-xs">
                    {txns.length} {txns.length === 1 ? "transaction" : "transactions"}
                  </Badge>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">
                    {formatAmount(totalAmount)}
                  </span>
                  {txns.length > 3 && (
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                      {isExpanded ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* Transactions */}
            <AnimatePresence mode="sync">
              {isExpanded && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="space-y-2"
                >
                  {displayTxns.map((txn) => {
                    const Icon = getIcon(txn);
                    const isExpanded = expandedTxns.has(txn.id);

                    return (
                      <motion.div
                        key={txn.id}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="rounded-lg border bg-card p-4 hover:border-primary/50 transition-all"
                      >
                        <div
                          className="flex items-center justify-between cursor-pointer"
                          onClick={() => showDetails && toggleTransaction(txn.id)}
                        >
                          <div className="flex items-center gap-3 flex-1">
                            <div
                              className={cn(
                                "flex h-10 w-10 items-center justify-center rounded-full",
                                txn.type === "CREDIT"
                                  ? "bg-green-500/10"
                                  : "bg-red-500/10"
                              )}
                            >
                              {txn.type === "CREDIT" ? (
                                <ArrowDownRight className="h-5 w-5 text-green-600" />
                              ) : (
                                <ArrowUpRight className="h-5 w-5 text-red-600" />
                              )}
                            </div>

                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate">
                                {txn.merchantName || txn.description || "Transaction"}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                {formatTime(txn.createdAt)}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center gap-3">
                            <p
                              className={cn(
                                "font-semibold tabular-nums",
                                txn.type === "CREDIT"
                                  ? "text-green-600"
                                  : "text-red-600"
                              )}
                            >
                              {txn.type === "CREDIT" ? "+" : "-"}
                              {formatAmount(txn.amountCents)}
                            </p>

                            {showDetails && (
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                {isExpanded ? (
                                  <ChevronUp className="h-4 w-4" />
                                ) : (
                                  <ChevronDown className="h-4 w-4" />
                                )}
                              </Button>
                            )}
                          </div>
                        </div>

                        {/* Expanded details */}
                        <AnimatePresence>
                          {showDetails && isExpanded && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              exit={{ opacity: 0, height: 0 }}
                              className="mt-4 space-y-2 border-t pt-4"
                            >
                              <div className="grid grid-cols-2 gap-3 text-sm">
                                <div>
                                  <span className="text-muted-foreground">Transaction ID</span>
                                  <p className="font-mono">{txn.id.slice(0, 8)}...</p>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">Status</span>
                                  <p className="font-medium">{txn.status || "Completed"}</p>
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    );
                  })}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        );
      })}
    </motion.div>
  );
}
